import React from "react";
import ReactDOM from 'react-dom';
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";

import { ImgSelect } from "@components/Images/ImgSelect";
import { ImgCancel } from "@components/Images/ImgCancel";

import { defaulComparator, stringSearchService } from "@services/StringSearchService";
import apiService from "@services/apiService";

import type { ImageGenModel } from "./ImageModelPicker";

type ImageStyle = {
  name: string;
  displayName: string;
  isHidden?: boolean;
  keywords?: string[];
}

export type ImageStyleFound = ImageStyle & {
  isNameMatch: boolean;
  keywordsMatched: string[];
}

type ImageStylePickerProps = {
  model: ImageGenModel;
  selectedStyles: string[];
  onChange: (selectedStyles: string[]) => void;
  deferLoad?: boolean;  // when true, skip loading on mount until set to false
};

export function parseLoraStyleName(loraName: string): ImageStyle {
  let styleName = loraName;
  // remove .safetensors suffix if present
  if (styleName.endsWith('.safetensors')) {
    styleName = styleName.slice(0, '.safetensors'.length * -1);
  }

  // find clip skip marker (_CS[Number], _ is optional) and remove it woth number. Examples: _CS2, _CS1
  styleName = styleName.replace(/(_)?CS\d{1}/, '');

  // replace strenht marker (_S[value], _ is optional) with ' (Strength: value)'. Examples: _S07, S10
  styleName = styleName.replace(/(_)?S(\d{1,2})/, '');

  // remove model prefixes
  for (const prefix of ['Z-Image\\', 'SDXL\\', 'Flux\\']) {
    if (styleName.startsWith(prefix)) {
      styleName = styleName.replace(prefix, '');
      break;
    }
  }

  // extract (and remove) keywords (comma separated) from the end of the style name in brackets. Example: (keyword1, keyword2)
  const keywords = [];
  const keywordMatch = styleName.match(/\(([^)]+)\)$/);
  if (keywordMatch) {
    const keywordsStr = keywordMatch[1];
    styleName = styleName.replace(/\(([^)]+)\)$/, '');
    keywords.push(...keywordsStr.split(',').map(k => k.trim()));
  }
  
  // trim whitespace
  styleName = styleName.trim();

  // replace underscores (and -) with spaces, every word capitalized. Example: "my_style-name" -> "My Style Name"
  styleName = styleName.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return {
    name: loraName,
    displayName: styleName,
    keywords: keywords.length > 0 ? keywords : undefined
  };
}

@observer
class ImageStylePicker extends React.Component<ImageStylePickerProps> {
  @observable _showStylePicker: boolean = false;
  @observable _styleSearchQuery: string = '';
  @observable _styles: ImageStyle[] | null = null;
  @observable _modelName: ImageGenModel | null = null;
  @observable private _isShiftPressed: boolean = false;
  @observable private _isClosing: boolean = false;
  private _dropdownRect: { left: number; width: number; bottom: number } | null = null;
  private _triggerRef = React.createRef<HTMLButtonElement>();
  private _dropdownRef = React.createRef<HTMLDivElement>();

  constructor(props: ImageStylePickerProps) {
    super(props);
    this._modelName = props.model;
    makeObservable(this);
  }

  // on props change - reset selection if model changes
  componentDidUpdate(prevProps: ImageStylePickerProps) {
    if (prevProps.model !== this.props.model) {
      this._modelName = this.props.model;
      this.props.onChange([]);
    }
    // load styles when deferLoad transitions from true → false
    if (prevProps.deferLoad && !this.props.deferLoad && this._styles === null) {
      void this._loadStyles();
    }
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._handleKeyDown);
    document.addEventListener('keyup', this._handleKeyUp);
    if (!this.props.deferLoad) {
      void this._loadStyles();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._handleKeyDown);
    document.removeEventListener('keyup', this._handleKeyUp);
  }

  private async _loadStyles() {
    // load all styles and then filter based on model on render
    const loras = await apiService.get<{ loras: { name: string, isHidden: boolean }[] }>('/api/image/lora');
    runInAction(() => {
      const styles: ImageStyle[] = loras.loras.map(lora => {
        const result = parseLoraStyleName(lora.name);
        result.isHidden = lora.isHidden;
        return result;
      });
      this._styles = styles;
    });
  }

  @computed
  get searchTerms(){
    const searchStr = this._styleSearchQuery;
    return searchStr.split(/[ ,]+/).filter(x => !!x);
  }

  @computed
  private get filteredStyles(): ImageStyle[] {
    let filteredList = this._styles || [];

    if (!this._isShiftPressed){
      // Filter out hidden styles
      filteredList = filteredList.filter(style => !style.isHidden);
    }
    
    // Apply search filter
    const searchStringParts = this.searchTerms;
    if (searchStringParts.length) {
      filteredList = filteredList.filter((style): boolean => {
        let result = true;

        for(const term of searchStringParts){
          let anyMatch = false;

          if (defaulComparator(style.name, term, 0) !== -1){
            anyMatch = true;
          }

          if (style.keywords && style.keywords.length > 0 && defaulComparator(style.keywords.join(' '), term, 0) !== -1){
            anyMatch = true;
          }

          if (!anyMatch){
              result = false;
          }
        }

        return result;
      });
    }
    
    return filteredList;
  }
  
  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  @action.bound
  private _onDocumentClick = (e: MouseEvent) => {
    const isClickInWrapper = (e.target as HTMLElement).closest('.style-picker-wrapper') !== null;
    const isClickInDropdown = this._dropdownRef.current?.contains(e.target as Node) ?? false;
    if (isClickInWrapper || isClickInDropdown) return;
    if (this._showStylePicker && !this._isClosing) {
      this._isClosing = true;
    }
  }

  @action.bound
  private _handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this._showStylePicker && !this._isClosing) {
      this._isClosing = true;
    }
    this._isShiftPressed = e.shiftKey;
  }

  @action.bound
  private _handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = false;
    }
  }

  @computed
  private get _modelPrefix(): string {
    let prefixForModel = 'NoNe\\';
    if (this._modelName === 'zImage'){
      prefixForModel = 'Z-Image\\';
    } else if (this._modelName === 'SDXL'){
      prefixForModel = 'SDXL\\';
    } else if (this._modelName === 'Flux1.Dev'){
      prefixForModel = 'Flux\\';
    }
    return prefixForModel;
  }

  render(){
    const { selectedStyles } = this.props;

    const styles = this._styles;
    if (!styles) {
      // Still loading — render placeholder so layout doesn't shift
      return (
        <div className="control-line">
          <label>Styles:</label>
          <div className="style-picker-wrapper">
            <button className="style-picker-trigger" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>
              Loading…
            </button>
          </div>
        </div>
      );
    }

    let prefixForModel = this._modelPrefix;

    const filteredStyles = this.filteredStyles.filter(style =>
      style.name.startsWith(prefixForModel)
    );

    const allStylesForModel = (this._styles || []).filter(style =>
      style.name.startsWith(prefixForModel) && (!style.isHidden || this._isShiftPressed)
    );
    const noStylesForModel = allStylesForModel.length === 0 && selectedStyles.length === 0;

    if (noStylesForModel) {
      return (
        <div className="control-line">
          <label>Styles:</label>
          <div className="style-picker-wrapper">
            <button className="style-picker-trigger" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
              No styles for this model
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="control-line">
        <label>Styles:</label>
        <div className="style-picker-wrapper">
          
          <button
            ref={this._triggerRef}
            className="style-picker-trigger"
            onClick={this._toggleStylePicker}
          >
            {selectedStyles.length === 0 && 'Select styles (max 3)'}
            {selectedStyles.length > 0 && (
            <div className="selected-styles-display">
              {selectedStyles.map(styleId => {
                const style = styles.find(s => s.name === styleId);
                return style ? (
                  /* keywords as title */
                  <div key={styleId} className="selected-style-chip" title={style.keywords?.join(', ')}>
                    <img loading="lazy" src={`/api/image/lora/preview/${encodeURIComponent(style.name)}`} alt={style.displayName} className="chip-preview" />
                    <span className="chip-name">{style.displayName}</span>
                    <span className="chip-remove" onClick={(e) => { 
                      e.stopPropagation(); 
                      this._removeStyle(styleId); 
                    }}><ImgCancel size={12} /></span>
                  </div>
                ) : null;
              })}
            </div>
          )}
            <span className="dropdown-arrow">{this._showStylePicker ? '▲' : '▼'}</span>
          </button>
          {(this._showStylePicker || this._isClosing) && ReactDOM.createPortal(
            <div
              ref={this._dropdownRef}
              className={'style-picker-dropdown' + (this._isClosing ? ' closing' : '')}
              onAnimationEnd={this._onDropdownAnimationEnd}
              style={{
                position: 'fixed',
                left: this._dropdownRect?.left ?? 0,
                bottom: this._dropdownRect?.bottom ?? 0,
                width: this._dropdownRect?.width ?? 0,
              }}
            >
              <div className="style-search-wrapper">
                <input
                  type="text"
                  className="style-search-input"
                  placeholder="Search styles..."
                  value={this._styleSearchQuery}
                  onChange={this._onStyleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="style-options-list">
                {filteredStyles.length > 0 ? (
                  filteredStyles.map(style => (
                    <div
                      key={style.name}
                      className={'style-option' + (selectedStyles.includes(style.name) ? ' selected' : '')}
                      onClick={() => this._toggleStyle(style.name)}
                    >
                      <img loading="lazy" src={`/api/image/lora/preview/${encodeURIComponent(style.name)}`} alt={style.displayName} className="style-preview" />
                      <span className="style-name">{this._renderText(style.displayName)}</span>
                      {selectedStyles.includes(style.name) && <span className="checkmark"><ImgSelect size={14} /></span>}
                    </div>
                  ))
                ) : (
                  <div className="no-results">No styles found</div>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    );
  }
  
  @action
  private _toggleStylePicker = () => {
    if (this._showStylePicker) {
      this._isClosing = true;
      return;
    }
    if (this._triggerRef.current) {
      const rect = this._triggerRef.current.getBoundingClientRect();
      this._dropdownRect = {
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + 4,
      };
    }
    this._showStylePicker = true;
    this._isClosing = false;
    setTimeout(() => {
      const input = this._dropdownRef.current?.querySelector('.style-search-input') as HTMLInputElement | null;
      if (input) input.focus();
    }, 0);
  }

  @action
  private _onDropdownAnimationEnd = () => {
    if (this._isClosing) {
      this._showStylePicker = false;
      this._isClosing = false;
      this._styleSearchQuery = '';
    }
  }

  @action.bound
  private _onStyleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    this._styleSearchQuery = e.target.value;
  }

  @action.bound
  private _toggleStyle(styleId: string) {
    const { selectedStyles, onChange } = this.props;
    const index = selectedStyles.indexOf(styleId);
    if (index > -1) {
      selectedStyles.splice(index, 1);
    } else {
      if (selectedStyles.length < 3) {
        selectedStyles.push(styleId);
      }
    }
    onChange([...selectedStyles]);
  }

  @action.bound
  private _removeStyle(styleId: string) {
    const { selectedStyles, onChange } = this.props;
    const index = selectedStyles.indexOf(styleId);
    if (index > -1) {
      selectedStyles.splice(index, 1);
    }
    onChange([...selectedStyles]);
  }
}

export default ImageStylePicker;